<template>
    <div v-show="opened">
        <div
            v-if="active"
            :style="styles"
            ref="keyboardDeploymentAutocomplete"
            class="keyboard-deployment-autocomplete"
            @click="toggleDropdownOnClick"
            @contextmenu.prevent
        >
            <div class="arrow" :style="{left: arrowLeft + 'px'}"></div>
            <div :style="styles" class="keyboard-deployment-autocomplete__holder">
                <ul class="keyboard-deployment-autocomplete__holder__dropdown">
                    <li
                        v-for="(availableShift, index) in filteredShifts"
                        :key="index"
                        :class="{'active': index === activeIndex}"
                        @click="clickShift(index)"
                        class="keyboard-deployment-autocomplete__holder__dropdown__item"
                    >
                        <span class="shiftLabel" v-html="getFormattedCode(availableShift.template!.code)"></span>
                        <KeyboardDeploymentAutocompleteSpecification :specifications="availableShift.template!.specifications" />
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import {KeyControlSetting} from '@/components/deploymentAutocomplete/types';
    import {Deployment, Company, CompanyEmployee, Shift, PersonalStampShift, PersonalStamp, OrganisationDeploymentState, DeploymentSpecification, Organisation, DeploymentProposal, DeploymentState} from '@cquential/api';
    import {OrganisationUtil} from '@/util/organisation';
    import cloneDeep from 'lodash/cloneDeep';
    import DeploymentStateMapping from '@/util/deploymentstatemapping';
    import {CellCoordinates, Copyable, PlanbordGridRow, RosterData} from '@/types/types';
    import escapeRegExp from 'lodash/escapeRegExp';
    import DeploymentUtil from '@/util/deploymentutil';
    import Notify from '@/components/modals/Notify.vue';
    import ShiftStateUpdateModal from '@/components/modals/ShiftStateUpdateModal.vue';
    import WarningInformationModal from '../modals/WarningInformationModal.vue';
    import ShortcutModal from '@/components/modals/ShortcutModal.vue';
    import Dates from '@cquential/date-utils';
    import {ResourceWithKeyedByDateObjects, CompanyEmployeeWithDeployments, CompanyWithDeploymentsAndEmployees} from '@/types/api-object-extensions';
    import StampShiftEditModal from '@/components/modals/StampShiftEditModal.vue';
    import {ShortcutType} from '@/types/enums/ShortcutType';
    import {CSSProperties, nextTick} from 'vue';
    import Planbord from '@/components/Planbord.vue';
    import PlanbordGrid from '@/components/Planbord/PlanbordGrid.vue';
    import ClipboardUtils from '@/util/ClipboardUtils';
    import KeyboardDeploymentAutocompleteSpecification from '@/components/deploymentAutocomplete/KeyboardDeploymentAutocompleteSpecification.vue';
    import {Component, Vue, Prop, Watch, Ref} from 'vue-facing-decorator';
    import PlanbordCell from '@/components/Planbord/PlanbordCell.vue';
    import CellCapacity from '@/util/CellCapacity';

    @Component({
        components: {
            KeyboardDeploymentAutocompleteSpecification,
        },
        emits: [
            'copy:cells',
            'create:stampShift',
            'create:stampShifts',
            'update:stampShift',
            'delete:stampShift',
            'delete:cells',
            'create:deployment',
            'update:deployment',
            'delete:deployment',
            'create:deploymentProposal',
            'update:deploymentProposal',
            'delete:deploymentProposal',
            'save:deployments',
        ],
    })
    export default class KeyboardDeploymentAutocomplete extends Vue {
        @Prop({required: true}) public organisation!: Organisation;
        @Prop({required: true}) public shiftIds!: number[];
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop() public shortcutPreferences?: Record<string, KeyControlSetting>;

        @Ref() public readonly keyboardDeploymentAutocomplete!: HTMLDivElement;

        private static instance: KeyboardDeploymentAutocomplete | null = null;

        private readonly COLUMN_OVERFLOW_WIDTH: number = 5; // also change variable in CSS (below)
        private readonly COLUMN_OVERFLOW_LEFT: number = 130;
        private readonly ALLOWED_SHIFT_CODE_SYMBOLS: string[] = ['(', ')', '*', '@', '-']; // note that minus is not actually allowed, but we generally use it as "no shift", thus we allow searching for it

        private active: boolean = false;
        private arrowLeft: number = 0;

        // Define rect
        private top: number = 0;
        private left: number = 0;
        private width: number = 0;

        // Define input (typed text)
        private input: string = '';
        private opened: boolean = false;
        private openTimeout: number | undefined = undefined;

        // Active index in results array (invoked by keyboard navigation)
        private activeIndex: number = 0;
        private cell: PlanbordCell | undefined = undefined;
        private manuallySelectedShiftId: number = -1;
        private stampDayIndex: number | null = null;
        private date: Date = new Date();
        private deployment: Deployment | undefined = undefined;
        private personalStamp: PersonalStamp | undefined = undefined;
        private personalStampShift: PersonalStampShift | undefined = undefined;
        private resource: ResourceWithKeyedByDateObjects | undefined = undefined;
        private roster: RosterData | undefined = undefined;
        private company: Company | undefined = undefined;
        private companyEmployee: CompanyEmployee | undefined = undefined;
        private proposed: boolean = false;
        private lastKeyIsControl: boolean = false;
        private cellsToCut: CellCoordinates[] = [];
        private shortcutModalOpen: boolean = false;
        private isDeploymentProposal: boolean = false;

        private superHackyScrollAllow: number = 0;
        private deleteKeyDisabled: boolean = false;

        @Watch('input')
        public onInputChange(): void {
            this.search();
        }

        @Watch('activeIndex')
        public onActiveIndexChange(): void {
            this.makeSureSelectedShiftIsVisible();
        }

        public get filteredShifts(): Shift[] {
            if (!this.date) {
                return [];
            }

            return Object.values(this.shiftIds)
                .map((id: number): Shift => this.shifts[id])
                .filter((shift: Shift): boolean => this.date >= shift.beginDate
                    && this.date <= shift.endDate
                    && (shift.template!.code.toLowerCase().indexOf(this.input.toLowerCase()) > -1),
                )
                .sort((a: Shift, b: Shift): number => a.template!.code.localeCompare(b.template!.code));
        }

        // Returns the OS specific control key
        public get controlKey(): string {
            return /^Mac/.test(navigator.platform) ? 'Meta' : 'Control';
        }

        // Set styles to component to render at the right position
        public get styles(): CSSProperties {
            return {
                top: this.top + 'px',
                left: (this.left + 1 - this.COLUMN_OVERFLOW_WIDTH - this.COLUMN_OVERFLOW_LEFT) + 'px',
                width: (this.width) ? (this.width - 1 + (this.COLUMN_OVERFLOW_WIDTH * 2) + this.COLUMN_OVERFLOW_LEFT) + 'px' : '',
                maxHeight: 'calc(100vh - ' + (this.top + 25) + 'px)',
                display: 'block',
            };
        }

        public get isInStampArea(): boolean {
            if (!this.resource) {
                return false;
            }

            if (!this.resource.stampDeployedUntil) {
                return true;
            }

            return this.resource.stampDeployedUntil < this.date;
        }

        // reference to a Vue component doesn't work properly in a getter -- I think TODO figure this out properly or just... rewrite the whole thing
        public get canEditSelectedCell(): boolean {
            return this.cell !== undefined && this.cell.editable;
        }

        public get canProposeInSelectedCell(): boolean {
            return this.cell !== undefined && this.cell.canProposeDeployments;
        }

        public mounted(): void {
            KeyboardDeploymentAutocomplete.instance = this;

            window.addEventListener('keydown', this.keyboardDownEventHandler);
            window.addEventListener('keyup', this.keyboardUpEventHandler);
            window.addEventListener('wheel', this.wheelEventHandler, {passive: true});

            document.querySelector('#planbord-holder')!.addEventListener('scroll', this.hide);
            document.body.addEventListener('click', this.hide); // hide the dropdown when anything outside of it is clicked
            window.addEventListener('blur', this.hide); // hide the dropdown whenever you blur the browser window/tab
            window.addEventListener('resize', this.hide); // we could try to force it to fit, but if you're resizing the window, you don't care about the dropdown anymore, do you?
        }

        public beforeUnmount(): void {
            KeyboardDeploymentAutocomplete.instance = null;

            window.removeEventListener('keydown', this.keyboardDownEventHandler);
            window.removeEventListener('keyup', this.keyboardUpEventHandler);
            window.removeEventListener('wheel', this.wheelEventHandler);

            document.querySelector('#planbord-holder')!.removeEventListener('scroll', this.hide);
            document.body.removeEventListener('click', this.hide);
            window.removeEventListener('blur', this.hide);
            window.removeEventListener('resize', this.hide);
        }

        public lastKeyIsNotControl(): void {
            this.lastKeyIsControl = false;
        }

        public activate(
            index: number,
            tableCell: PlanbordCell,
            willOpenDropdown: boolean,
            newPersonalStamp: PersonalStamp | undefined,
            newPersonalStampShift: PersonalStampShift | undefined,
            isProposal: boolean = false,
        ): void {
            // Set data
            this.cell = tableCell;
            this.date = tableCell.day.date;
            this.deployment = typeof index !== 'undefined' ? tableCell.deployments[index] : undefined;
            this.resource = tableCell.resource;
            this.roster = tableCell.roster;
            this.company = tableCell.company;
            this.companyEmployee = tableCell.companyEmployee;
            this.proposed = tableCell.day.proposed;
            // Set active to false to not render before we know if activecell is an element
            this.active = false;
            this.opened = willOpenDropdown;
            this.personalStamp = newPersonalStamp;
            this.personalStampShift = newPersonalStampShift;
            this.isDeploymentProposal = isProposal;

            if (this.personalStamp) {
                this.stampDayIndex = tableCell.stampDayIndex ?? null;
            }

            if (this.openTimeout) {
                clearTimeout(this.openTimeout);
            }

            // Set activeIndex to false to reinitialize keyboard nav in choices
            this.activeIndex = 0;
            if (tableCell) {
                this.active = this.canEditSelectedCell || isProposal;
            } else {
                console.error('Element is not found');
            }

            if (willOpenDropdown) {
                this.openDropdown(index);
            } else if (this.singleCellSelectedWhereShiftCanBeAdded(tableCell)) {
                this.openTimeout = setTimeout((): void => {
                    this.openDropdown(index);
                }, 500);
            }
        }

        public updateDeployment(updateDeployment: Deployment, oldId: number): void {
            if (this.deployment && this.deployment.id === oldId) {
                this.deployment = updateDeployment;
            }
        }

        // Method to reset values (disable the whole component)
        public disable(): void {
            this.left = 0;
            this.top = 0;
            this.width = 0;
            this.active = false;
            this.input = '';
            this.opened = false;
        }

        // Navigate up in choices
        public navigateChoicesUp(): void {
            this.navigateChoices('up');
        }

        // Navigate down in choices
        public navigateChoicesDown(): void {
            this.navigateChoices('down');
        }

        public singleCellSelectedWhereShiftCanBeAdded(tableCell: PlanbordCell): boolean {
            /** @see PlanbordGrid.getSelectedCells() */
            // @ts-expect-error TS2339
            return (!tableCell.isInStampPeriod || this.personalStamp !== undefined) && PlanbordGrid.__vfdConstructor.getSelectedCells().length < 2;
        }

        public hide(): void {
            if (this.superHackyScrollAllow > 0) {
                this.superHackyScrollAllow--;

                return;
            }
            this.opened = false;
        }

        public disableDeleteKey(): void {
            this.deleteKeyDisabled = true;
        }

        public enableDeleteKey(): void {
            this.deleteKeyDisabled = false;
        }

        // Method that will emit a custom event to open ShiftStateUpdateModal
        public openShiftEditDialog(forceNew: boolean = false): void {
            if (!this.isEmployed()) {
                return;
            }

            if (this.canProposeInSelectedCell) {
                if (this.isInStampArea || !this.resource) {
                    return;
                }

                // TODO 6853 const proposal: DeploymentProposal | false = (deploymentProposal.value && 'id' in deploymentProposal.value) ? deploymentProposal.value : false;
                // TODO 6853 ShiftStateUpdateModal.openModal(resource.value, company.value, forceNew ? false : proposal, date.value);
                /** @see ShiftStateUpdateModal.openModal() */
                // @ts-expect-error TS2339
                ShiftStateUpdateModal.__vfdConstructor.openModal(this.resource, undefined, false, this.date, true);
                this.hide();
            }

            if (!this.canEditSelectedCell) {
                return;
            }

            if (this.isInStampArea) {
                if (!this.resource || !this.personalStamp || !('id' in this.personalStamp) || !this.personalStamp.personalStampShifts) {
                    return;
                }

                const stampShift: PersonalStampShift | null = (this.personalStampShift && 'id' in this.personalStampShift) ? this.personalStampShift : null;

                /** @see StampShiftEditModal.openModal() */
                // @ts-expect-error TS2339
                StampShiftEditModal.__vfdConstructor.openModal(this.resource!, this.personalStamp, forceNew ? null : stampShift, this.date);
                this.hide();

                return;
            }

            const deploymentModel: Deployment | false = (this.deployment && 'id' in this.deployment) ? this.deployment : false;
            /** @see ShiftStateUpdateModal.openModal() */
            // @ts-expect-error TS2339
            ShiftStateUpdateModal.__vfdConstructor.openModal(this.resource, this.company, forceNew ? false : deploymentModel, this.date);
            this.hide();
        }

        // Toggle dropdown visibility
        public toggleDropdownOnClick(): void {
            this.opened = !this.opened;
        }

        // Catch keyboard events and fire other methods when recognised
        public keyboardDownEventHandler(e: KeyboardEvent): void {
            if (!e.ctrlKey && !e.metaKey && !e.altKey && !this.lastKeyIsControl && !this.shortcutModalOpen && this.opened) {
                // note: ascii counts 0 1 2 3 ... 9 (as normal people do)
                // your keyboard (1 2 3 ... 9 0) is different
                // the if-statement needs to use the ascii order
                if ((e.key.length === 1 && e.key >= 'A' && e.key <= 'z') || (e.key.length === 1 && e.key >= '0' && e.key <= '9') || this.ALLOWED_SHIFT_CODE_SYMBOLS.indexOf(e.key) >= 0) {
                    this.input += e.key;
                    e.preventDefault();
                }

                if (e.key === 'Backspace') {
                    if (this.input.length) {
                        this.input = this.input.substring(0, this.input.length - 1);
                    }
                    e.preventDefault();
                }

                if (e.key === 'Enter') {
                    this.save();
                }
            }

            /** @see ShortcutModal.isWaitingForInput() */
            // @ts-expect-error TS2339
            if (e.repeat || ShortcutModal.__vfdConstructor.isWaitingForInput()) {
                return;
            }

            // check if modal is open to prevent reopening the modal -> shortcutModelOpen
            this.lastKeyIsControl = (e.key === this.controlKey && !this.shortcutModalOpen);

            if (e.key === 'Delete' && !this.deleteKeyDisabled && this.canEditSelectedCell) {
                this.deleteShifts();
            }

            let action: ShortcutType | null = null;

            for (const shortcut in this.shortcutPreferences) {
                const shortcutPreference: KeyControlSetting = this.shortcutPreferences[shortcut as ShortcutType];
                if (this.isRightKeyCombinationPressed(shortcutPreference, e)) {
                    action = shortcut as ShortcutType;
                }
            }

            if (action) {
                // if we know the keyboard shortcut, prevent the browsers default action (if they let us), regardless of whether we actually perform the action
                e.preventDefault();
            }

            switch (action) {
                case ShortcutType.copy:
                    this.copyShifts();
                    break;
                case ShortcutType.paste:
                    if (this.canEditSelectedCell) {
                        this.pasteShifts();
                    }
                    break;
                case ShortcutType.cut:
                    if (this.canEditSelectedCell) {
                        this.cutShifts();
                    }
                    break;
                case ShortcutType.editDeployment:
                    /** @see PlanbordGrid.getSelectedCells() */
                    // @ts-expect-error TS2339
                    if ((this.canEditSelectedCell || this.canProposeInSelectedCell) && PlanbordGrid.__vfdConstructor.getSelectedCells().length < 2) {
                        this.openShiftEditDialog();
                    }
                    break;
                case ShortcutType.newDeployment:
                    /** @see PlanbordGrid.getSelectedCells() */
                    // @ts-expect-error TS2339
                    if ((this.canEditSelectedCell || this.canProposeInSelectedCell) && PlanbordGrid.__vfdConstructor.getSelectedCells().length < 2) {
                        this.openShiftEditDialog(true);
                    }
                    break;
                case ShortcutType.normal:
                    if (this.canEditSelectedCell) {
                        this.updateState(DeploymentState.Normal);
                    }
                    break;
                case ShortcutType.free:
                    if (this.canEditSelectedCell) {
                        this.updateState(DeploymentState.Free);
                    }
                    break;
                case ShortcutType.sick:
                    if (this.canEditSelectedCell) {
                        this.updateState(DeploymentState.Sick);
                    }
                    break;
                case ShortcutType.extra:
                    if (this.canEditSelectedCell) {
                        this.updateState(DeploymentState.Extra);
                    }
                    break;
                case ShortcutType.training:
                    if (this.canEditSelectedCell) {
                        this.updateState(DeploymentState.Training);
                    }
                    break;
                case ShortcutType.compensationLeave:
                    if (this.canEditSelectedCell) {
                        this.updateState(DeploymentState.CompensationLeave);
                    }
                    break;
                case ShortcutType.specialLeave:
                    if (this.canEditSelectedCell) {
                        this.updateState(DeploymentState.SpecialLeave);
                    }
                    break;
            }

            if (e.ctrlKey || e.metaKey || this.shortcutModalOpen) {
                this.shortcutModalOpen = false;
                /** @see ShortcutModal.closeModal() */
                // @ts-expect-error TS2339
                ShortcutModal.__vfdConstructor.closeModal();
            }
        }

        public isRightKeyCombinationPressed(shortcutPreference: KeyControlSetting, e: KeyboardEvent): boolean {
            return shortcutPreference.key === e.key
                && shortcutPreference.ctrlKey === e.ctrlKey
                && shortcutPreference.metaKey === e.metaKey
                && shortcutPreference.shiftKey === e.shiftKey
                && shortcutPreference.altKey === e.altKey;
        }

        public keyboardUpEventHandler(e: KeyboardEvent): void {
            if (e.key === this.controlKey && this.lastKeyIsControl) {
                this.shortcutModalOpen = true;
                /** @see ShortcutModal.show() */
                // @ts-expect-error TS2339
                ShortcutModal.__vfdConstructor.show();
            }
        }

        public wheelEventHandler(): void {
            this.lastKeyIsControl = false;
        }

        // Method to update deployments
        public updateState(state: DeploymentState): Promise<void> | void {
            let deployments: Deployment[] = [];
            /** @see PlanbordGrid.getSelectedCells() */
            // @ts-expect-error TS2339
            const selectedCells: CellCoordinates[] = PlanbordGrid.__vfdConstructor.getSelectedCells();

            if (selectedCells.length < 2 && this.deployment) {
                deployments.push(this.deployment);
            } else {
                /** @see PlanbordGrid.getPlanbordCellsFromCoordinates() */
                // @ts-expect-error TS2339
                deployments = PlanbordGrid.__vfdConstructor.getPlanbordCellsFromCoordinates(selectedCells).map((cell: PlanbordCell): Deployment[] => cell.deployments).flat(2);
            }

            deployments = deployments.filter((deployment: Deployment): boolean => !deployment.updating);

            const updatedDeployments: Deployment[] = [];
            for (const deployment of deployments) {
                if (deployment && 'id' in deployment) {
                    if (!this.isStateAvailable(state, deployment)) {
                        continue;
                    }

                    const deploymentWithUpdatedState: Deployment = cloneDeep(deployment);

                    const mapping: Record<string, number> = DeploymentStateMapping.getStateMapping(this.organisation);
                    if (!(state in mapping)) {
                        throw new Error('Unknown state: ' + state.toString());
                    }

                    deploymentWithUpdatedState.stateId = mapping[state];

                    for (const stateNumber in this.organisation.organisationDeploymentStates) {
                        if (stateNumber === deploymentWithUpdatedState.stateId.toString()) {
                            deploymentWithUpdatedState.publicComment = this.organisation.organisationDeploymentStates[stateNumber].comment;
                            break;
                        }
                    }

                    if (deploymentWithUpdatedState.specifications) {
                        for (const specification of deploymentWithUpdatedState.specifications) {
                            specification.stateId = deploymentWithUpdatedState.stateId;
                        }
                    }

                    updatedDeployments.push(deploymentWithUpdatedState);
                }
            }

            this.$emit('save:deployments', updatedDeployments);
        }

        public isStateAvailable(state: DeploymentState, givenDeployment: Deployment | undefined = undefined): boolean {
            if (!givenDeployment) {
                givenDeployment = this.deployment;
            }

            if (!givenDeployment) {
                return false;
            }

            const deploymentStates: Record<number, OrganisationDeploymentState> = this.organisation.organisationDeploymentStates;
            for (const deploymentStateIndex in deploymentStates) {
                const deploymentState: OrganisationDeploymentState = deploymentStates[deploymentStateIndex];
                if (deploymentState && deploymentState.id === state) {
                    return deploymentState.beginDate <= givenDeployment.date
                        && deploymentState.endDate >= givenDeployment.date;
                }
            }

            return false;
        }

        public addDeploymentsToClipboard(deployments: Deployment[], offsetLeft: number, offsetTop: number): Copyable[] {
            return deployments.map((deployment: Deployment): Copyable => {
                return {
                    type: 'deployment',
                    offsetLeft,
                    offsetTop,
                    object: cloneDeep(deployment),
                };
            });
        }

        public addDeploymentProposalsToClipboard(deploymentProposals: DeploymentProposal[], offsetLeft: number, offsetTop: number): Copyable[] {
            return deploymentProposals.map((deploymentProposal: DeploymentProposal): Copyable => {
                return {
                    type: 'deployment',
                    offsetLeft,
                    offsetTop,
                    object: DeploymentUtil.convertDeploymentProposalToDeployment(deploymentProposal),
                };
            });
        }

        public addStampShiftsToClipboard(stampShifts: PersonalStampShift[], offsetLeft: number, offsetTop: number): Copyable[] {
            return stampShifts.map((stampShift: PersonalStampShift): Copyable => {
                return {
                    type: 'stampShift',
                    offsetLeft,
                    offsetTop,
                    object: cloneDeep(stampShift),
                };
            });
        }

        public copyShifts(): Copyable[] {
            this.hide();
            /** @see PlanbordGrid.getSelectedCells() */
            // @ts-expect-error TS2339
            const newClipboardContent: Copyable[] = this.getCopyablesFromCells(PlanbordGrid.__vfdConstructor.getSelectedCells());
            if (newClipboardContent.length === 0) {
                /** @see Notify.createNotification() */
                // @ts-expect-error TS2339
                Notify.__vfdConstructor.createNotification({text: 'Er is geen dienst beschikbaar om te kopiëren', type: 'error'});
            }

            ClipboardUtils.writeCopyables(newClipboardContent);

            /** @see PlanbordGrid.deselectAllCells() */
            // @ts-expect-error TS2339
            PlanbordGrid.__vfdConstructor.deselectAllCells();
            this.cellsToCut = [];

            this.$emit('copy:cells', newClipboardContent);

            return newClipboardContent;
        }

        public getCopyablesFromCells(selectedCells: CellCoordinates[]): Copyable[] {
            let copyables: Copyable[] = [];
            /** @see PlanbordGrid.sortCellCoordinates() */
            // @ts-expect-error TS2339
            const sortedCells: CellCoordinates[] = PlanbordGrid.__vfdConstructor.sortCellCoordinates(selectedCells);

            /** @see PlanbordGrid.getFirstSelectedCell() */
            // @ts-expect-error TS2339
            const firstSelectedCell: CellCoordinates | null = PlanbordGrid.__vfdConstructor.getFirstSelectedCell();
            /** @see PlanbordGrid.getLastSelectedCell() */
            // @ts-expect-error TS2339
            const lastSelectedCell: CellCoordinates | null = PlanbordGrid.__vfdConstructor.getLastSelectedCell();

            if (!firstSelectedCell || !lastSelectedCell) {
                return [];
            }

            const xDiff: number = Math.min(firstSelectedCell.x, lastSelectedCell.x);
            const yDiff: number = Math.min(firstSelectedCell.y, lastSelectedCell.y);

            for (const coordinates of sortedCells.values()) {
                /** @see PlanbordGrid.getRow() */
                // @ts-expect-error TS2339
                const row: PlanbordGridRow | undefined = PlanbordGrid.__vfdConstructor.getRow(coordinates.y);
                const planbordCell: PlanbordCell | undefined = row ? row.getCell(coordinates.x) : undefined;

                if (planbordCell) {
                    if (planbordCell.deployments.length > 0) {
                        copyables = copyables.concat(this.addDeploymentsToClipboard(planbordCell.deployments, coordinates.x - xDiff, coordinates.y - yDiff));
                    }

                    if (planbordCell.deploymentProposals.length > 0) {
                        copyables = copyables.concat(this.addDeploymentProposalsToClipboard(planbordCell.deploymentProposals, coordinates.x - xDiff, coordinates.y - yDiff));
                    }

                    if (planbordCell.stampShifts.length > 0) {
                        copyables = copyables.concat(this.addStampShiftsToClipboard(planbordCell.stampShifts, coordinates.x - xDiff, coordinates.y - yDiff));
                    }
                }
            }

            return copyables;
        }

        public pasteShifts(): void {
            this.hide();

            ClipboardUtils.readCopyables()
                .then((clipboardItems: Copyable[]): void => {
                    if (clipboardItems.length > 0) {
                        this.applyCopiedShiftsToResourcesAndStamps(clipboardItems);
                    }
                    this.deleteCutCellContents();
                })
                .catch((): void => {
                    /** @see Notify.createNotification() */
                    // @ts-expect-error TS2339
                    Notify.__vfdConstructor.createNotification({text: 'Er is geen dienst beschikbaar om te plakken', type: 'error'});
                });
        }

        public isCopyableTypeAllowedInPersonalStamp(copyable: Copyable): boolean {
            return copyable.type === 'stampShift' || copyable.type === 'deployment';
        }

        public isShiftApplicableOnCurrentRoster(shiftId: number): boolean {
            return this.filteredShifts.some((shiftInRoster: Shift): boolean => {
                return shiftInRoster.id === shiftId;
            });
        }

        public applyCopiedShiftsToResourcesAndStamps(copyables: Copyable[]): void {
            if (!this.roster) {
                return;
            }

            const deploymentsToSave: Deployment[] = [];
            const personalStampShiftsToSave: PersonalStampShift[] = [];
            const stampResourceIds: number[] = [];
            const firstStampPasteDateForResource: Record<number, Date> = {};

            for (const copyable of copyables) {
                const dateToPaste: Date = Dates.Utils.addDays(this.date, copyable.offsetLeft);
                const dayIndex: number = this.cell!.dayIndex! + copyable.offsetLeft;
                const rowIndex: number = this.cell!.resourceIndex! + copyable.offsetTop;
                /** @see PlanbordGrid.getRow() */
                // @ts-expect-error TS2339
                const row: PlanbordGridRow | undefined = PlanbordGrid.__vfdConstructor.getRow(rowIndex);
                const pasteTargetCell: PlanbordCell | undefined = row ? row.getCell(dayIndex) : undefined;

                // Also prevents pasting cells that are outside the visible roster
                if (!pasteTargetCell || !this.canCopyablesBePastedInCell(pasteTargetCell)) {
                    continue;
                }

                const resourceId: number | null = pasteTargetCell.resource ? pasteTargetCell.resource.id! : null;
                const companyId: number | null = pasteTargetCell.company ? pasteTargetCell.company.id! : null;
                const companyEmployeeId: number | null = pasteTargetCell.companyEmployee ? pasteTargetCell.companyEmployee.id! : null;
                const personalStamp: PersonalStamp | null = pasteTargetCell.stamp ?? null;

                if (personalStamp && resourceId) {
                    if (!(rowIndex in firstStampPasteDateForResource)) {
                        firstStampPasteDateForResource[rowIndex] = dateToPaste;
                    }
                    let stampDayIndexToPaste: number;

                    if (personalStamp.beginDate > this.cell!.day.date) {
                        const stampIndexOffsetRelativeToSelectedCell: number = Dates.Utils.diffInDays(personalStamp.beginDate, this.cell!.day.date);
                        const stampIndexOffsetSinceBeginDate: number = Dates.Utils.diffInDays(personalStamp.beginDate, pasteTargetCell.day.date) % personalStamp.iterationLength;
                        stampDayIndexToPaste = Dates.Utils.diffInDays(personalStamp.beginDate, dateToPaste) + copyable.offsetLeft - stampIndexOffsetRelativeToSelectedCell - stampIndexOffsetSinceBeginDate;
                    } else {
                        stampDayIndexToPaste = (Dates.Utils.diffInDays(this.cell!.day.date, personalStamp.beginDate) % personalStamp.iterationLength) + copyable.offsetLeft;
                    }

                    if (!this.isCopyableTypeAllowedInPersonalStamp(copyable)) {
                        /** @see WarningInformationModal.open() */
                        // @ts-expect-error TS2339
                        WarningInformationModal.__vfdConstructor.open('U plakt een type dienst die niet is toegestaan in een stempel', 'Niet alle diensten geplakt');
                        continue;
                    }

                    if (Dates.Utils.diffInDays(dateToPaste, firstStampPasteDateForResource[rowIndex]) >= personalStamp.iterationLength) {
                        /** @see WarningInformationModal.open() */
                        // @ts-expect-error TS2339
                        WarningInformationModal.__vfdConstructor.open('U plakt meer dagen dan voor het einde van de stempel passen. Niet alle diensten zijn geplakt.', 'Niet alle diensten geplakt');
                        continue;
                    }

                    if (!this.isShiftApplicableOnCurrentRoster(copyable.object.shiftId)) {
                        /** @see WarningInformationModal.open() */
                        // @ts-expect-error TS2339
                        WarningInformationModal.__vfdConstructor.open('U plakt een dienst uit een ander rooster. Diensten uit een ander rooster kunnen niet in een stempel geplakt worden', 'Niet alle diensten geplakt');
                        continue;
                    }

                    if (!stampResourceIds.includes(resourceId)) {
                        stampResourceIds.push(resourceId);
                    }

                    personalStampShiftsToSave.push(this.convertCopyableToPersonalStampShift(copyable, stampDayIndexToPaste, personalStamp.id!));
                } else {
                    deploymentsToSave.push(this.convertCopyableToDeployment(copyable, dateToPaste, resourceId, companyId, companyEmployeeId));
                }

                this.activeIndex = 0;
            }

            if (deploymentsToSave.length > 0) {
                this.saveDeployments(deploymentsToSave);
            }
            if (personalStampShiftsToSave.length > 0) {
                this.saveStampShifts(stampResourceIds, personalStampShiftsToSave);
            }
        }

        public canCopyablesBePastedInCell(cell: PlanbordCell): boolean {
            return !!cell.resource || !!cell.company;
        }

        public saveDeployments(deploymentsToSave: Deployment[]): void {
            if (deploymentsToSave.length === 1) {
                this.$emit('create:deployment', deploymentsToSave[0]);
            } else if (deploymentsToSave.length > 1) {
                this.$emit('save:deployments', deploymentsToSave);
            }
        }

        public saveStampShifts(resourceIds: number[], personalStampShifts: PersonalStampShift[]): void {
            if (resourceIds.length === 1 && personalStampShifts.length === 1) {
                this.createPersonalStampShift(personalStampShifts[0], resourceIds[0]);

                return;
            }
            this.createPersonalStampShifts(personalStampShifts, resourceIds);
        }

        public convertCopyableToDeployment(copyable: Copyable, dateToPaste: Date, resourceId: number | null = null, companyId: number | null = null, companyEmployeeId: number | null = null): Deployment {
            if (copyable.type === 'deployment' && 'id' in copyable.object) {
                const deployment: Deployment = cloneDeep(copyable.object);

                return this.getClonedRosterDeploymentOnDate(deployment, dateToPaste, resourceId, companyId, companyEmployeeId);
            }

            return this.getCopyableAsDeploymentOnDate(copyable, dateToPaste, resourceId, companyId, companyEmployeeId);
        }

        public convertCopyableToPersonalStampShift(copyable: Copyable, indexToPaste: number, personalStampId: number): PersonalStampShift {
            return {
                stampId: personalStampId,
                dayIndex: indexToPaste,
                shiftId: copyable.object.shiftId,
            };
        }

        public getClonedRosterDeploymentOnDate(deployment: Deployment, dateToPaste: Date, resourceId: number | null = null, companyId: number | null = null, companyEmployeeId: number | null = null): Deployment {
            if ('id' in deployment) {
                delete deployment.id;
            }

            if (resourceId) {
                deployment.resourceId = resourceId;
                deployment.companyId = null;
            }
            if (companyId) {
                deployment.companyId = companyId;
                deployment.resourceId = null;

                if (companyEmployeeId) {
                    deployment.companyEmployeeId = companyEmployeeId;
                }
            }

            return DeploymentUtil.cloneWithNewDate(deployment, dateToPaste);
        }

        public getCopyableAsDeploymentOnDate(copyable: Copyable, dateToPaste: Date, resourceId: number | null = null, companyId: number | null = null, companyEmployeeId: number | null = null): Deployment {
            const defaultStateId: number = OrganisationUtil.getDefaultState(this.organisation.organisationDeploymentStates);

            return {
                date: dateToPaste,
                shiftId: copyable.object.shiftId,
                stateId: defaultStateId,
                publicComment: null,
                resourceId: resourceId,
                companyId: companyId,
                companyEmployeeId: companyEmployeeId,
            };
        }

        public deleteCutCellContents(): void {
            if (this.cellsToCut.length > 0) {
                this.deleteSelectedCells(this.cellsToCut);
                Object.assign(this.cellsToCut, []);
            }
        }

        // Method to cut and copy selected shift(s)
        public cutShifts(): void {
            /** @see PlanbordGrid.getSelectedCells() */
            // @ts-expect-error TS2339
            const coordinatesCopy: CellCoordinates[] = cloneDeep(PlanbordGrid.__vfdConstructor.getSelectedCells());
            this.copyShifts();
            this.cellsToCut = coordinatesCopy;
        }

        public deleteShifts(): void {
            this.hide();
            /** @see PlanbordGrid.getSelectedCells() */
            // @ts-expect-error TS2339
            const selectedCells: CellCoordinates[] = PlanbordGrid.__vfdConstructor.getSelectedCells();

            if (selectedCells.length > 1) {
                this.deleteSelectedCells(selectedCells);
                /** @see PlanbordGrid.deselectAllCells() */
                // @ts-expect-error TS2339
                PlanbordGrid.__vfdConstructor.deselectAllCells();

                return;
            }
            this.deleteShift();
        }

        public deleteShift(): void {
            if (this.personalStampShift === undefined && this.deployment === undefined) {
                return;
            }
            this.disableDeleteKey();

            if (this.personalStampShift) {
                this.deletePersonalStampShift(this.personalStampShift);
                nextTick((): void => {
                    this.reactivateSelectedCell();
                });
            }
            if (this.deployment && 'id' in this.deployment) {
                this.$emit('delete:deployment', this.deployment);
                this.reactivateSelectedCell(); // TODO REMOVE STORE: should we postpone  emits are not promises, alas
            }
        }

        public reactivateSelectedCell(): void {
            /** @see PlanbordGrid.getActiveCell() */
            // @ts-expect-error TS2339
            const activeCell: CellCoordinates | null = PlanbordGrid.__vfdConstructor.getActiveCell();
            /** @see PlanbordGrid.resetGrid() */
            // @ts-expect-error TS2339
            PlanbordGrid.__vfdConstructor.resetGrid();
            if (activeCell) {
                const x: number = activeCell.x;
                const y: number = activeCell.y;
                const index: number = this.getMarkerIndexToReactivateTo(activeCell);
                /** @see Planbord.setActiveCell() */
                // @ts-expect-error TS2339
                Planbord.__vfdConstructor.setActiveCell(x, y, false, false, index);
            }
            // A hard reset, needed to prevent a side effect which completely blocks future (delete) actions
            this.enableDeleteKey();
        }

        public getMarkerIndexToReactivateTo(activeCell: CellCoordinates): number {
            if (this.cellContainsExactlyOneMarker() || activeCell.index === undefined) {
                return 0;
            }
            if (this.selectedMarkerIsLastMarker(activeCell)) {
                return activeCell.index - 1;
            }

            return activeCell.index;
        }

        public cellContainsExactlyOneMarker(): boolean {
            return this.cell!.stampShifts.length === 1 || this.cell!.deployments.length === 1;
        }

        public selectedMarkerIsLastMarker(activeCell: CellCoordinates): boolean {
            const lastPossibleIndex: number = this.cell!.stampShifts.length + this.cell!.deployments.length;

            return activeCell.index === lastPossibleIndex;
        }

        public deleteSelectedCells(selectedCells: CellCoordinates[]): void {
            let deploymentsToDelete: Deployment[] = [];
            let stampShiftsToDelete: PersonalStampShift[] = [];
            const resources: Record<number, ResourceWithKeyedByDateObjects> = [];
            const companies: Record<number, CompanyWithDeploymentsAndEmployees> = [];
            const companyEmployees: Record<number, CompanyEmployeeWithDeployments> = [];

            for (const coordinates of selectedCells) {
                /** @see PlanbordGrid.getRow() */
                // @ts-expect-error TS2339
                const row: PlanbordGridRow | undefined = PlanbordGrid.__vfdConstructor.getRow(coordinates.y);
                const planbordCell: PlanbordCell | undefined = row ? row.getCell(coordinates.x) : undefined;

                if (planbordCell) {
                    if (planbordCell.resource?.id && !resources[planbordCell.resource.id]) {
                        resources[planbordCell.resource.id] = planbordCell.resource;
                    }

                    if (planbordCell.company?.id && !companies[planbordCell.company.id]) {
                        companies[planbordCell.company.id] = planbordCell.company;

                        if (planbordCell.companyEmployee?.id) {
                            companyEmployees[planbordCell.companyEmployee.id] = planbordCell.companyEmployee;
                        }
                    }

                    // Cell contains deployments
                    if (planbordCell.deployments) {
                        deploymentsToDelete = deploymentsToDelete.concat(planbordCell.deployments);
                    }
                    // Cell contains personalStampShifts
                    if (planbordCell.stampShifts) {
                        stampShiftsToDelete = stampShiftsToDelete.concat(planbordCell.stampShifts);
                    }
                }
            }

            this.$emit('delete:cells', resources, companies, deploymentsToDelete, stampShiftsToDelete, companyEmployees);
        }

        public openDropdown(index: number): void {
            if (this.isDeploymentProposal) {
                // TODO 6854: actually do something, instead of... nothing
                return;
            } else if (!this.canEditSelectedCell) {
                // nothing to open
                return;
            }

            this.superHackyScrollAllow = 1; // allow the scroll from the function below

            const arrowBaseElement: HTMLElement | null
                = this.cell!.$el.querySelector('.deployments-holder > *:nth-child(' + (index + 1) + ') .deploymentMarker, .deployment-container > .isProposed:nth-child(' + (index + 1) + ') .deploymentMarker, .stamp-holder > *:nth-child(' + (index + 1) + ') .deploymentMarker');

            /** @see Planbord.scrollActiveCellIntoView() */
            // @ts-expect-error TS2339
            Planbord.__vfdConstructor.scrollActiveCellIntoView(arrowBaseElement !== null);
            setTimeout((): void => {
                // doesn't seem to work in $nextTick (even if I do 4), so let's just use a timeout and hope it's long enough even on slow systems
                this.superHackyScrollAllow = 0;
            }, 50);

            // wait for scroll of planbord
            nextTick((): void => {
                if (arrowBaseElement === null) {
                    return;
                }

                // Set coords
                this.setPositionOnPage(this.cell!.$el, arrowBaseElement);
                this.opened = true;
                this.manuallySelectedShiftId = -1;
                this.search();

                // wait for render of dropdown
                nextTick((): void => {
                    this.makeSureSelectedShiftIsVisible();
                });
            });
        }

        public setActiveIndexToDeployment(): void {
            let selectedId: number;
            if (this.manuallySelectedShiftId >= 0) {
                selectedId = this.manuallySelectedShiftId;
            } else if (this.deployment) {
                selectedId = this.deployment.shiftId;
            } else if (this.personalStampShift) {
                selectedId = this.personalStampShift.shiftId;
            } else {
                selectedId = -1;
            }

            const selectedIndex: number = this.filteredShifts.findIndex((shift: Shift): boolean => shift.id === selectedId);
            this.activeIndex = selectedIndex === -1 ? 0 : selectedIndex;
        }

        public setPositionOnPage(element: Element, arrowBaseElement: HTMLElement): void {
            const arrowBoundingRect: DOMRect = arrowBaseElement.getBoundingClientRect();
            const arrowBoundingMiddle: number = (arrowBoundingRect.right + arrowBoundingRect.left) / 2;

            const rectangle: DOMRect = element.getBoundingClientRect();
            this.left = rectangle.left;
            this.width = rectangle.width;
            this.top = arrowBoundingRect.bottom + 9;
            this.arrowLeft = -10 + arrowBoundingMiddle - this.left + this.COLUMN_OVERFLOW_LEFT;
        }

        public search(): void {
            // If there are no filetered results, reset input to current
            if (Object.keys(this.filteredShifts).length === 0) {
                // Set value to current deployment
                this.input = '';
            }

            this.setActiveIndexToDeployment();
        }

        // Method to save chosen shift
        public save(): void {
            if (!this.resource && !this.company) {
                throw new Error('Cannot save deployment if no resource/company is known.');
            }

            if (!this.opened) {
                return;
            }

            const shiftToSave: Shift = this.filteredShifts[this.activeIndex];

            // Not too clean but the way whole dropdown thing works will be cleaned up later on
            if (this.personalStamp) {
                this.savePersonalStampShift(this.personalStampShift);

                return;
            }

            if (!this.deployment && !shiftToSave.template!.isDefault) {
                const stateId: number = OrganisationUtil.getDefaultState(this.organisation.organisationDeploymentStates);
                const specifications: DeploymentSpecification[] | undefined = DeploymentUtil.createSpecificationsFromTemplateSpecifications(this.date, shiftToSave.template!.specifications, stateId);
                const newDeployment: Deployment = {
                    publicComment: null,
                    date: this.date,
                    shiftId: shiftToSave.id,
                    stateId,
                    resourceId: (this.resource && this.resource.id) ? this.resource.id : null,
                    companyId: this.company ? this.company.id : null,
                    companyEmployeeId: this.companyEmployee?.id ?? null,
                    companyEmployee: this.companyEmployee ?? undefined,
                    specifications,
                };

                this.createDeployment(newDeployment);

                setTimeout((): void => {
                    this.opened = false;
                }, 0);
                this.activeIndex = 0;
            } else if (shiftToSave.template!.isDefault && this.deployment && 'id' in this.deployment) {
                this.deleteDeployment(this.deployment);

                setTimeout((): void => {
                    this.opened = false;
                }, 0);
                this.activeIndex = 0;
            } else if (this.deployment && this.deployment.shiftId !== shiftToSave.id && 'id' in this.deployment) {
                const deploymentClone: Deployment = cloneDeep(this.deployment);
                deploymentClone.shiftId = shiftToSave.id;
                deploymentClone.specified = false;
                deploymentClone.specifications = DeploymentUtil.createSpecificationsFromTemplateSpecifications(this.date, shiftToSave.template!.specifications, deploymentClone.stateId);
                this.changeDeployment(deploymentClone);

                setTimeout((): void => {
                    this.opened = false;
                }, 0);
                this.activeIndex = 0;
            }

            this.opened = false;
        }

        public createDeployment(deployment: Deployment): void {
            if (this.isDeploymentProposal) {
                this.$emit('create:deploymentProposal', DeploymentUtil.convertDeploymentToDeploymentProposal(deployment));
            } else {
                this.$emit('create:deployment', deployment);
            }
        }

        public deleteDeployment(deployment: Deployment): void {
            if (this.isDeploymentProposal) {
                this.$emit('delete:deploymentProposal', DeploymentUtil.convertDeploymentToDeploymentProposal(deployment));
            } else {
                this.$emit('delete:deployment', deployment);
            }
        }

        public changeDeployment(deployment: Deployment): void {
            if (this.isDeploymentProposal) {
                this.$emit('update:deploymentProposal', DeploymentUtil.convertDeploymentToDeploymentProposal(deployment));
            } else {
                this.$emit('update:deployment', deployment);
            }
        }

        public savePersonalStampShift(stampShift: PersonalStampShift | undefined): void {
            const shiftToSave: Shift = this.filteredShifts[this.activeIndex];

            // Existing shift was given, update the shift to the shift selected by the user
            if (stampShift && stampShift.id) {
                if (!shiftToSave.template!.isDefault) {
                    this.updatePersonalStampShift(stampShift);
                } else {
                    // Default option was selected, delete the shift on position
                    this.deletePersonalStampShift(stampShift);
                }
            } else if (stampShift === undefined && !shiftToSave.template!.isDefault) {
                // No existing shifts were given, create a new shift
                stampShift = {
                    stampId: this.personalStamp!.id!,
                    dayIndex: this.stampDayIndex!,
                    shiftId: shiftToSave.id,
                };
                this.createPersonalStampShift(stampShift, this.resource!.id!);
            }
        }

        public createPersonalStampShift(stampShift: PersonalStampShift, resourceIdToRefresh: number): void {
            this.$emit('create:stampShift', {shift: stampShift, resourceId: resourceIdToRefresh});
        }

        public createPersonalStampShifts(stampShifts: PersonalStampShift[], resourceIds: number[]): void {
            this.$emit('create:stampShifts', {shifts: stampShifts, resourceIds: resourceIds});
        }

        public updatePersonalStampShift(stampShift: PersonalStampShift): void {
            const stampShiftClone: PersonalStampShift = cloneDeep(stampShift);
            stampShiftClone.shiftId = this.filteredShifts[this.activeIndex].id;

            this.$emit('update:stampShift', {resourceId: this.resource!.id!, shift: stampShiftClone});
        }

        public deletePersonalStampShift(stampShift: PersonalStampShift): void {
            this.$emit('delete:stampShift', {shift: stampShift, resourceId: this.resource!.id!});
        }

        public getFormattedCode(shiftCode: string): string {
            return shiftCode.replace(new RegExp(escapeRegExp(this.input), 'gi'), (foundText: string): string => '<span class="searchedText">' + foundText + '</span>');
        }

        public clickShift(index: number): void {
            this.activeIndex = index;
            this.save();
        }

        public navigateChoices(dir: string): void {
            // If null (startingValue), set to 1 (0 is always occupied by the current shift)
            if (dir === 'down' && (this.activeIndex + 1) < this.filteredShifts.length) {
                this.activeIndex++;
            }

            if (dir === 'up' && this.activeIndex > 0) {
                this.activeIndex--;
            }

            this.manuallySelectedShiftId = this.filteredShifts[this.activeIndex].id;
        }

        // Navigate trough choices
        public makeSureSelectedShiftIsVisible(): void {
            const selectedOption: HTMLElement | null = document.querySelector('.keyboard-deployment-autocomplete__holder__dropdown li:nth-child(' + (this.activeIndex + 1) + ')');
            const list: HTMLElement | null = document.querySelector('.keyboard-deployment-autocomplete__holder');

            if (!selectedOption || !list) {
                return;
            }

            const selectedOptionRectangle: DOMRect = selectedOption.getBoundingClientRect();
            const listRectangle: DOMRect = list.getBoundingClientRect();

            if (selectedOptionRectangle.top < listRectangle.top) {
                list.scrollTop -= listRectangle.top - selectedOptionRectangle.top;
            } else if (selectedOptionRectangle.bottom > listRectangle.bottom) {
                list.scrollTop += selectedOptionRectangle.bottom - listRectangle.bottom;
            }
        }

        // doesn't work as a getter, probably because entire thing is one big UGLY AND BAD hack
        public isEmployed(): boolean {
            return CellCapacity.mayContainDeployments(this.date, this.resource, this.company, this.companyEmployee);
        }

        // Static wrapper methods for keyboard utility functionality
        public static disable(): void {
            this.instance!.disable();
        }

        public static lastKeyIsNotControl(): void {
            this.instance!.lastKeyIsNotControl();
        }

        public static activate(
            index: number,
            tableCell: PlanbordCell,
            openDropdown: boolean,
            newPersonalStamp: PersonalStamp | undefined,
            newPersonalStampShift: PersonalStampShift | undefined,
            isProposal: boolean = false,
        ): void {
            this.instance!.activate(index, tableCell, openDropdown, newPersonalStamp, newPersonalStampShift, isProposal);
        }

        public static updateDeployment(updateDeployment: Deployment, oldId: number): void {
            this.instance!.updateDeployment(updateDeployment, oldId);
        }

        public static navigateChoicesUp(): void {
            this.instance!.navigateChoicesUp();
        }

        public static navigateChoicesDown(): void {
            this.instance!.navigateChoicesDown();
        }
    }
</script>

<style lang="scss" scoped>
    .keyboard-deployment-autocomplete {
        --overflow-column-width: 5px; // also change constant in javascript
        display: none;
        position: absolute;
        top: -100px;
        left: -100px;
        z-index: 2002;
        box-sizing: border-box;

        .arrow {
            border-left: 1px solid var(--shift-dropdown-border-color);
            border-top: 1px solid var(--shift-dropdown-border-color);
            position: absolute;
            width: 16px;
            height: 16px;
            z-index: 1;
            transform: translate(var(--overflow-column-width), -3px) rotate(45deg);
            background: linear-gradient(135deg, var(--shift-dropdown-background) 0%, var(--shift-dropdown-background) 55%, transparent 55%);
        }

        &__holder {
            display: inline-block;
            background-color: transparent;
            border: 1px solid var(--shift-dropdown-border-color);
            border-radius: 4px;
            overflow: hidden auto;
            margin-top: 5px;

            &__input-holder {
                width: 0;
                height: 0;
                overflow: hidden;

                &__input {
                    width: 0;
                    height: 0;
                    opacity: 0;
                }
            }

            &__dropdown {
                position: relative;
                margin: 0;
                list-style-type: none;
                padding: 0;
                background-color: var(--shift-dropdown-background);
                border-bottom: 1px var(--shift-dropdown-separator-color) solid;

                &__item {
                    padding: .2rem;
                    padding-left: .5rem;
                    border-width: 0 1px 1px;
                    border-color: var(--shift-dropdown-separator-color);
                    border-style: solid;
                    cursor: pointer;
                    word-break: keep-all;
                    white-space: nowrap;
                    overflow: hidden;

                    &:hover {
                        background-color: var(--shift-dropdown-highlight-background);
                    }

                    &:last-child {
                        border-bottom: none;
                    }

                    &.active {
                        background-color: var(--shift-dropdown-active-background);
                    }
                }
            }
        }
    }

    :deep(.searchedText) {
        font-weight: 800; // the difference between 700 and 800 is huge (but 400->700 is "meh")
    }
</style>
